// @ts-strict-ignore
import {
  getChatWindowGreetingClickedPropertyName,
  getChatWindowGreetingSkippedPropertyName,
} from 'helpers/agent-custom-properties';
import { EventPlace } from 'helpers/analytics';
import { anyToBoolean, booleanToNumericString, type BooleanNumericFlag } from 'helpers/boolean';
import { browserMajorVersion, browserName } from 'helpers/browser';
import { getNpsFirstChat } from 'helpers/get-nps-first-chat';
import { isGhostLogin } from 'helpers/ghost';
import { type KeyMap } from 'helpers/interface';
import { navigate } from 'helpers/routing';
import { type Properties } from 'interfaces/property';
import { getLoggedInAgent } from 'store/entities/agents/selectors';
import { IntegrationLicensePropertiesNames } from 'store/entities/integration-license-properties/interfaces';
import { AgentCustomPropertiesActions } from 'store/features/agent-custom-properties/actions';
import { AgentCustomPropertyName } from 'store/features/agent-custom-properties/interfaces';
import {
  getChatWindowClickedGreetingCount,
  getChatWindowSkippedGreetingCount,
  getChatWindowSkippedGreetingsCount,
  hasFetchedAgentCustomProperties,
} from 'store/features/agent-custom-properties/selectors';

import { AppStateProvider } from './app-state-provider';
import { isGhostLogin as isGhostLoginNew } from './auth/auth-storage-manager';
import { trackEvent } from './event-tracking';

interface IChatEventData {
  visibility: 'maximized' | 'minimized' | 'hidden';
  status: 'chatting' | 'browsing' | 'invited';
}

export interface IChatWidget {
  // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
  _h: any;
  init(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  call(eventName: string, args?: any): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(eventName: string, handler: (eventData: any) => void): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(eventName: string, handler: (eventData: any) => void): void;
  get(propName: string): KeyMap;
}
interface IGreetingPayload {
  id: number;
  uniqueId: string;
}

interface IRichGreetingActionPayload {
  eventId: string;
  greeting: IGreetingPayload;
  postbackId: string;
}

export type ISessionVariables = Partial<{
  [`logout reason`]: 'API response';
  [`og_campaign_ecommerce_agent_trainer`]: boolean;
  [`Both_app_licenses`]: boolean;
  [`WAB_licenses`]: boolean;
  [`Messenger_licenses`]: boolean;
  [`EMP_licenses`]: boolean;
  [`CRM_licenses`]: boolean;
}>;

const ecommerceCampaignPropertiesMap = {
  [IntegrationLicensePropertiesNames.CampaignEcommerceAgentTrainer]: 'og_campaign_ecommerce_agent_trainer',
  [IntegrationLicensePropertiesNames.CampaignEcommerceBothAppLicenses]: 'Both_app_licenses',
  [IntegrationLicensePropertiesNames.CampaignEcommerceWabLicenses]: 'WAB_licenses',
  [IntegrationLicensePropertiesNames.CampaignEcommerceMessengerLicenses]: 'Messenger_licenses',
  [IntegrationLicensePropertiesNames.CampaignEcommerceEmpLicenses]: 'EMP_licenses',
  [IntegrationLicensePropertiesNames.CampaignEcommerceCrmLicenses]: 'CRM_licenses',
} as const;

/**
 * Session variable names are intentionally 1:1 with the property names, so that Engage can use
 * the same targeting condition in the Agent App and in the Text App.
 */
const migrationCampaignPropertiesMap = {
  [IntegrationLicensePropertiesNames.CurrentMigrationBatch]: 'current_migration_batch',
  [IntegrationLicensePropertiesNames.CampaignMigrationDecided]: 'campaign_migration_decided',
} as const;

let widgetInitRequested = false;

/**
 * `LiveChatWidget.init()` has no guard against being called more than once — it appends a new
 * tracking script tag every time, and `_h` only flips once that script has loaded. Guarded calls
 * in the same synchronous flow (e.g. `setSessionVariables` followed by `openChatWidget`, or two
 * campaign updates back to back) would otherwise all see `_h` as falsy and all trigger `init()`.
 * Track the request locally to make it idempotent across every call site in this module.
 */
function ensureWidgetInitialized(isGhost: boolean): void {
  // eslint-disable-next-line no-underscore-dangle
  if (widgetInitRequested || window?.LiveChatWidget?._h || isGhost) {
    return;
  }

  widgetInitRequested = true;
  window.LiveChatWidget.init();
}

export function openChatWidget(): void {
  ensureWidgetInitialized(isGhostLogin());
  window.LiveChatWidget.call('maximize');
}

export function sendMessage(message: string): void {
  // eslint-disable-next-line no-underscore-dangle
  if (!window.LiveChatWidget._h && !isGhostLoginNew()) {
    ensureWidgetInitialized(isGhostLoginNew());
    window.LiveChatWidget.on('ready', () => {
      window.LiveChatWidget.call('send_message', { text: message });
    });

    return;
  }
  window.LiveChatWidget.call('send_message', { text: message });
}

export function setSessionVariables(variables: ISessionVariables): void {
  ensureWidgetInitialized(isGhostLogin());
  window.LiveChatWidget.call('set_session_variables', variables);
}

export function updateFirstChatDate(firstChatDate: Date | null): void {
  window.LiveChatWidget.call('update_session_variables', {
    /* eslint-disable @typescript-eslint/naming-convention */
    og_cv_browser_name: browserName,
    og_cv_browser_version: browserMajorVersion,
    og_cv_nps_first_chat: getNpsFirstChat(firstChatDate),
    /* eslint-enable @typescript-eslint/naming-convention */
  });
}

export function updateEcommerceCampaignSessionVariables(licenseProperties: Properties[string]): void {
  const sessionVariablesToUpdate = {};

  Object.entries(ecommerceCampaignPropertiesMap).forEach(([key, value]) => {
    if (licenseProperties[key]) {
      sessionVariablesToUpdate[value] = true;
    }
  });

  if (Object.keys(sessionVariablesToUpdate).length === 0) {
    return;
  }

  ensureWidgetInitialized(isGhostLogin());
  window.LiveChatWidget.call('update_session_variables', sessionVariablesToUpdate);
}

/**
 * Deliberately separate from `updateEcommerceCampaignSessionVariables`: that one sends a literal
 * `true` for every truthy property, and `'0'` is truthy in JS. Reusing it would send `'0'` as
 * `true` and silently break the `campaign_migration_decided = 0` half of the targeting condition.
 */
export function updateMigrationCampaignSessionVariables(licenseProperties: Properties[string]): void {
  const sessionVariablesToUpdate: Record<string, BooleanNumericFlag> = {};

  Object.entries(migrationCampaignPropertiesMap).forEach(([propertyName, sessionVariableName]) => {
    const value = licenseProperties[propertyName];

    // A missing property means the license is outside the migration batch. Sending '0' to everyone
    // would force LiveChatWidget.init() (and the tracking.js load) for every user, not just the batch.
    if (value === undefined || value === null || value === '') {
      return;
    }

    sessionVariablesToUpdate[sessionVariableName] = booleanToNumericString(anyToBoolean(value));
  });

  // The migration workflow may only write `campaign_migration_decided` at the moment a decision is
  // made, which would leave the whole target audience without the property and the campaign never
  // firing. Treat "in the batch, no decision recorded" as "not decided yet".
  const batchVariable = migrationCampaignPropertiesMap[IntegrationLicensePropertiesNames.CurrentMigrationBatch];
  const decidedVariable = migrationCampaignPropertiesMap[IntegrationLicensePropertiesNames.CampaignMigrationDecided];

  if (sessionVariablesToUpdate[batchVariable] === '1' && !sessionVariablesToUpdate[decidedVariable]) {
    sessionVariablesToUpdate[decidedVariable] = '0';
  }

  if (Object.keys(sessionVariablesToUpdate).length === 0) {
    return;
  }

  ensureWidgetInitialized(isGhostLogin());
  window.LiveChatWidget.call('update_session_variables', sessionVariablesToUpdate);
}

class ChatWidget {
  lastCustomerStatus: string;

  setUpCallbacks = (): void => {
    window.LiveChatWidget.on('visibility_changed', (data: IChatEventData) => {
      if (data.visibility === 'maximized') {
        this.onChatWindowOpened();
      }
    });

    window.LiveChatWidget.on('customer_status_changed', (data: IChatEventData) => {
      if (data.status === 'chatting') {
        this.onChatStarted();
      }

      if (this.lastCustomerStatus === 'chatting' && ['browsing', 'invited'].includes(data.status)) {
        this.onChatEnded();
      }

      this.lastCustomerStatus = data.status;
    });

    window.LiveChatWidget.on('greeting_hidden', (greeting: IGreetingPayload) => {
      const state = AppStateProvider.getState();
      const isFetched = hasFetchedAgentCustomProperties(state);
      const greetingId = greeting.id.toString();

      if (isFetched) {
        const allGreetingsSkippedSoFar = getChatWindowSkippedGreetingsCount(state);

        AppStateProvider.dispatch(
          AgentCustomPropertiesActions.setAgentCustomProperty({
            [AgentCustomPropertyName.ChatWindowGreetingsSkipped]: allGreetingsSkippedSoFar + 1,
          }),
        );

        const greetingSkippedSoFar = getChatWindowSkippedGreetingCount(state, greetingId);
        const greetingSkippedPropertyName = getChatWindowGreetingSkippedPropertyName(greetingId);

        AppStateProvider.dispatch(
          AgentCustomPropertiesActions.setAgentCustomProperty({
            [greetingSkippedPropertyName]: greetingSkippedSoFar + 1,
          }),
        );
      }

      trackEvent('Greeting dismissed', EventPlace.OnboardingGreetings, {
        greetingId,
      });
    });

    window.LiveChatWidget.on('rich_message_button_clicked', (richMessage: IRichGreetingActionPayload) => {
      const { greeting, postbackId } = richMessage;
      if (greeting) {
        const greetingId = greeting.id.toString();
        const greetingClickedSoFar = AppStateProvider.selectFromStore(getChatWindowClickedGreetingCount, greetingId);
        const greetingClickedPropertyName = getChatWindowGreetingClickedPropertyName(greetingId);

        AppStateProvider.dispatch(
          AgentCustomPropertiesActions.setAgentCustomProperty({
            [greetingClickedPropertyName]: greetingClickedSoFar + 1,
          }),
        );

        trackEvent('Greeting action clicked', EventPlace.OnboardingGreetings, {
          greetingId,
          actionId: postbackId,
        });
      }
    });

    window.LiveChatWidget.on('ready', () => {
      const customerData = window.LiveChatWidget.get('customer_data');

      window.LiveChatWidget.call('update_session_variables', {
        /* eslint-disable-next-line @typescript-eslint/naming-convention */
        og_cv_is_returning: customerData.isReturning,
      });

      window.LiveChatWidget.call('set_navigation_handler', (path: string) => {
        navigate(path);
      });
    });
  };

  updateCustomerDetails = (): void => {
    if (isGhostLogin()) {
      return;
    }

    const agent = AppStateProvider.selectFromStore(getLoggedInAgent);

    if (agent) {
      window.LiveChatWidget.call('set_customer_name', agent.name);
      window.LiveChatWidget.call('set_customer_email', agent.login);
    }
  };

  onChatWindowOpened = (): void => {
    AppStateProvider.dispatch({ type: 'FEATURES/CHAT_WINDOW/CHAT_WINDOW_OPENED' });
  };

  onChatStarted = (): void => {
    AppStateProvider.dispatch({ type: 'FEATURES/CHAT_WINDOW/CHAT_STARTED' });
  };

  onChatEnded = (): void => {
    AppStateProvider.dispatch({ type: 'FEATURES/CHAT_WINDOW/CHAT_ENDED' });
  };
}

const widget = new ChatWidget();

export default widget;
